import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Avatar from "../components/Avatar";
import PerformanceBadge from "../components/PerformanceBadge";
import { FULL_MONTHS, thisMonth, thisYear } from "../lib/utils";
import { TrendingUp, TrendingDown, Award, AlertTriangle, Users, Activity } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";

// Stable chart prop refs (extracted to module scope to avoid re-allocating on every render)
const CHART_MARGIN = { top: 10, right: 10, left: -10, bottom: 0 };
const SCORE_DOMAIN = [0, 5];
const RADAR_TICK = { fontSize: 11, fill: "#64748b" };
const RADAR_RADIUS_TICK = { fontSize: 10, fill: "#94a3b8" };
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" };
const BAR_RADIUS = [8, 8, 0, 0];

const CAT_LABELS = {
  quality_average: "Quality of Care",
  reliability_average: "Reliability",
  documentation_average: "Documentation",
  health_safety_average: "Health & Safety",
  professionalism_average: "Professionalism",
  compliance_average: "Compliance",
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [homes, setHomes] = useState([]);
  const hmHomes = (user.assigned_homes && user.assigned_homes.length ? user.assigned_homes : (user.assigned_home ? [user.assigned_home] : []));
  const isMultiHomeHM = user.access_role === "House Manager" && hmHomes.length > 1;
  const [home, setHome] = useState(user.access_role === "House Manager" ? (isMultiHomeHM ? "ALL" : (hmHomes[0] || "")) : "ALL");
  const [month, setMonth] = useState(thisMonth());
  const [year, setYear] = useState(thisYear());

  useEffect(() => { api.get("/homes").then(({ data }) => setHomes(data)); }, []);

  useEffect(() => {
    api.get("/dashboard", { params: { home, month, year } }).then(({ data }) => setData(data));
  }, [home, month, year]);

  if (!data) return <div className="p-8 text-slate-500">Loading dashboard…</div>;

  const catData = Object.entries(CAT_LABELS).map(([k, label]) => ({
    name: label.split(" ")[0], full: label, value: data.category_averages[k] || 0,
  }));

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-small-caps">Overview</p>
          <h1 className="text-h1 mt-1">Care Metrics Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">{FULL_MONTHS[data.month - 1]} {data.year} · {home === "ALL" ? "All Homes" : home}</p>
        </div>
        <div className="flex flex-wrap gap-2.5" data-testid="dashboard-filters">
          {(user.access_role !== "House Manager" || isMultiHomeHM) && (
            <select value={home} onChange={(e) => setHome(e.target.value)} className="text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="dashboard-home-select">
              <option value="ALL">All Homes</option>
              {homes.map((h) => <option key={h.home_name} value={h.home_name}>{h.home_name}</option>)}
            </select>
          )}
          <select value={month} onChange={(e) => setMonth(+e.target.value)} className="text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="dashboard-month-select">
            {FULL_MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <input type="number" value={year} onChange={(e) => setYear(+e.target.value)} className="text-sm rounded-md border border-slate-200 bg-white px-3 py-2 w-24" data-testid="dashboard-year-input" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-kpi-cards">
        <KpiCard label="Team Average" value={data.team_average?.toFixed(2) || "0.00"} icon={Activity} sub={`${data.rows.length} staff scored`} testId="kpi-team-avg" />
        <KpiCard label="Top Performer" value={data.top_performer?.staff_name || "—"} icon={Award} sub={data.top_performer ? `${data.top_performer.overall_weighted_score} · ${data.top_performer.performance_band}` : "No data"} testId="kpi-top-performer" />
        <KpiCard label="Improving Staff" value={data.improving.length} icon={TrendingUp} sub="vs previous month" testId="kpi-improving" tone="emerald" />
        <KpiCard label="Needs Attention" value={data.needs_attention.length} icon={AlertTriangle} sub="< 3.0 score" testId="kpi-needs-attention" tone="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="nv-card p-5 lg:col-span-2">
          <h3 className="text-h3">Category Averages</h3>
          <p className="text-xs text-slate-500 mt-0.5">Average score (1–5) across all scored staff for the period</p>
          <div className="mt-4 h-64 min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} debounce={50}>
              <BarChart data={catData} margin={CHART_MARGIN}>
                <XAxis dataKey="name" tickLine={false} axisLine={false} fontSize={12} stroke="#64748b" />
                <YAxis domain={SCORE_DOMAIN} tickLine={false} axisLine={false} fontSize={12} stroke="#64748b" />
                <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, _, p) => [v, p.payload.full]} />
                <Bar dataKey="value" radius={BAR_RADIUS} fill="#0369a1" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="nv-card p-5">
          <h3 className="text-h3">Performance Profile</h3>
          <p className="text-xs text-slate-500 mt-0.5">Team-wide profile across categories</p>
          <div className="mt-4 h-64 min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} debounce={50}>
              <RadarChart data={catData} outerRadius="68%" margin={{ top: 8, right: 28, bottom: 8, left: 28 }}>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="name" tick={RADAR_TICK} />
                <PolarRadiusAxis domain={SCORE_DOMAIN} tick={RADAR_RADIUS_TICK} />
                <Radar dataKey="value" stroke="#059669" fill="#059669" fillOpacity={0.25} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="nv-card p-5" data-testid="needs-attention-list">
          <div className="flex items-center justify-between">
            <h3 className="text-h3">Needs Attention</h3>
            <span className="text-xs text-slate-400">{data.needs_attention.length} staff</span>
          </div>
          <ul className="mt-3 divide-y divide-slate-100">
            {data.needs_attention.length === 0 && <li className="py-3 text-sm text-slate-500">No staff below 3.0 this period.</li>}
            {data.needs_attention.map((r) => (
              <li key={`${r.home}-${r.staff_user_id}`} className="py-3 flex items-center gap-3">
                <Avatar name={r.staff_name} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{r.staff_name}</p>
                  <p className="text-xs text-slate-500">{r.home}</p>
                </div>
                <PerformanceBadge band={r.performance_band} score={r.overall_weighted_score} />
              </li>
            ))}
          </ul>
        </div>

        <div className="nv-card p-5" data-testid="improving-list">
          <div className="flex items-center justify-between">
            <h3 className="text-h3">Improving</h3>
            <span className="text-xs text-slate-400">{data.improving.length} staff</span>
          </div>
          <ul className="mt-3 divide-y divide-slate-100">
            {data.improving.length === 0 && <li className="py-3 text-sm text-slate-500">No improving staff yet (need 2+ months of data).</li>}
            {data.improving.map((r) => (
              <li key={`${r.home}-${r.staff_user_id}`} className="py-3 flex items-center gap-3">
                <Avatar name={r.staff_name} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{r.staff_name}</p>
                  <p className="text-xs text-slate-500">{r.home}</p>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                  <TrendingUp size={12} /> +{r.delta}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="nv-card overflow-hidden" data-testid="dashboard-roster-table">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-h3">Roster</h3>
          <span className="text-xs text-slate-400 flex items-center gap-1.5"><Users size={13} /> {data.rows.length}</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3 font-semibold">Staff</th>
              <th className="text-left px-3 py-3 font-semibold">Home</th>
              <th className="text-right px-3 py-3 font-semibold">Score</th>
              <th className="text-left px-3 py-3 font-semibold">Band</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={`${r.home}-${r.staff_user_id}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="px-5 py-3"><div className="flex items-center gap-3"><Avatar name={r.staff_name} size="sm" /><span className="font-medium text-slate-800">{r.staff_name}</span></div></td>
                <td className="px-3 py-3 text-slate-600">{r.home}</td>
                <td className="px-3 py-3 text-right font-semibold text-slate-800 tabular-nums">{r.overall_weighted_score?.toFixed(2)}</td>
                <td className="px-3 py-3"><PerformanceBadge band={r.performance_band} /></td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-500">No scores recorded for this period.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, sub, tone = "sky", testId }) {
  const tones = {
    sky: "bg-sky-50 text-sky-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <div className="nv-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <p className="text-small-caps">{label}</p>
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon size={16} /></div>
      </div>
      <p className="mt-3 text-[28px] leading-tight font-bold font-heading text-slate-900 truncate">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{sub}</p>
    </div>
  );
}
